import React from 'react';

import moment from 'moment-timezone';
import { roundNumber } from 'utilitys/helpers/general';
import { capitalizeWord } from 'utilitys/helpers/strings/stringsUtils';

import constants from 'components/common/Constants/constants';
import { CriticalityScoreContent } from 'components/common/CustomTooltipContent/criticalityScoreContent';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import MainTable from 'components/common/Tables/MainTable/MainTable';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { RenderActivityCodes } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/activityRelations/RenderActivityCodes';
import { RenderTitleAndValue } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/activityRelations/RenderTitleAndValue';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/activityRelations/activityRelations.module.scss';
import {
    columns,
    initialState,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/activityRelations/activityRelations.utils';
import { ConstraintsWrapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/activityRelations/constraintsWrapper';
import { useQueryGetRelationActivity } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/queries/relationActivityQuery';
import { CustomizedTooltip } from 'components/common';

const ActivityRelations = ({ id, activityType }: { id?: number | string; activityType?: string }) => {
    const { data, isLoading, isFetching } = useQueryGetRelationActivity({
        taskId: id,
    });
    const numOfPredecessors = data?.predeccessors?.length;
    const numOfSuccessors = data?.successors?.length;
    const precedingActivities = data?.precedingActivities;
    const succeedingActivites = data?.succeedingActivites;
    const precedingRisk = data?.precedingRisk;
    const succeedingRisk = data?.succeedingRisk;
    return (
        <div className={classes.activityRelationsContainer}>
            {isLoading && <OverlayWithSpinner />}
            {data && Object.keys(data).length > 0 && (
                <>
                    <div className={classes.mainDetails}>
                        <div>
                            <RenderTitleAndValue
                                title="Activity Status"
                                value={data.activityStatus}
                                className={classes.mainDetails_1}
                            />
                            <RenderTitleAndValue
                                title="Activity Type"
                                value={data.activityType}
                                className={classes.mainDetails_1}
                            />
                        </div>

                        <div>
                            <RenderTitleAndValue
                                title="Start Date"
                                value={moment(data.startDate).format(constants.formats.date.default)}
                                className={classes.mainDetails_2}
                            />
                            <RenderTitleAndValue
                                title="Finish Date"
                                value={moment(data.finishDate).format(constants.formats.date.default)}
                                className={classes.mainDetails_2}
                            />
                        </div>

                        <div>
                            <RenderTitleAndValue
                                title="Project week start"
                                value={data.projectWeekStart}
                                className={classes.mainDetails_3}
                            />
                            <RenderTitleAndValue
                                title="Project week finish"
                                value={data.projectWeekFinish}
                                className={classes.mainDetails_3}
                            />
                        </div>

                        <div>
                            <RenderTitleAndValue
                                title="Critical Path"
                                value={data.criticalPath ? 'Yes' : 'No'}
                                className={classes.mainDetails_4}
                            />
                            <CustomizedTooltip
                                triggerElement={
                                    <RenderTitleAndValue
                                        title="Criticality Score"
                                        value={roundNumber(data.criticalityScore)}
                                        className={classes.mainDetails_4}
                                    />
                                }
                                tooltipContent={
                                    <CriticalityScoreContent
                                        precedingActivities={precedingActivities}
                                        succeedingActivites={succeedingActivites}
                                        numOfPredecessors={numOfPredecessors}
                                        numOfSuccessors={numOfSuccessors}
                                        precedingRisk={precedingRisk}
                                        succeedingRisk={succeedingRisk}
                                    />
                                }
                            />
                        </div>

                        <div>
                            <div>
                                <RenderTitleAndValue
                                    title="WBS level"
                                    value={data.wbsLevel}
                                    className={classes.mainDetails_5}
                                />
                            </div>
                            <div>
                                <RenderTitleAndValue
                                    title="Completion %"
                                    value={capitalizeWord(data.typePercentComplete?.toLowerCase())}
                                    className={classes.mainDetails_5}
                                />
                            </div>
                        </div>
                    </div>

                    <ConstraintsWrapper value={data.activityConstraints} />

                    <RenderActivityCodes value={data?.activityCode} />

                    {activityType !== 'WBS' && (
                        <div className={classes.tablesWrapper}>
                            <WidgetWithTitle title={'Resources'}>
                                <div data-testid={'table-container'} className={classes.tableContainer}>
                                    <MainTable
                                        columns={columns}
                                        data={data?.resourceData || []}
                                        initialState={initialState}
                                        maxHeight={400}
                                        isLoading={isLoading || isFetching}
                                        noData={<NoData>No resources found for this activity</NoData>}
                                    />
                                </div>
                            </WidgetWithTitle>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ActivityRelations;
